import React, { useState } from "react";
import { base44 } from "../api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { BookOpen, Calendar, User, ArrowRight, Search, Filter } from "lucide-react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

export default function Blog() {
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ['blog-posts'],
    queryFn: async () => { try { return await base44.entities.BlogPost.filter({ published: true }, '-created_date'); } catch { return []; } },
    retry: false
  });

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title?.toLowerCase().includes(searchTerm.toLowerCase()) || post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || post.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const categoryLabels = { actualites:"Actualités", conseils:"Conseils", projets:"Projets", temoignages:"Témoignages" };
  const categoryColors = { actualites:"bg-blue-100 text-blue-800", conseils:"bg-green-100 text-green-800", projets:"bg-purple-100 text-purple-800", temoignages:"bg-orange-100 text-orange-800" };

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="gradient-bg text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} className="text-center">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-yellow-400" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Blog & Actualités</h1>
            <p className="text-xl text-gray-200">Toute l'actualité de l'immobilier et de R2B Entreprises</p>
          </motion.div>
        </div>
      </section>

      <section className="py-8 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input type="text" placeholder="Rechercher un article..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black" />
            </div>
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black">
              <option value="all">Toutes les catégories</option>
              <option value="actualites">Actualités</option>
              <option value="conseils">Conseils</option>
              <option value="projets">Projets</option>
              <option value="temoignages">Témoignages</option>
            </select>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => <div key={i} className="animate-pulse bg-white rounded-xl shadow-lg overflow-hidden"><div className="h-48 bg-gray-200"></div><div className="p-6"><div className="h-4 bg-gray-200 rounded mb-4"></div></div></div>)}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-16"><BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-400" /><p className="text-xl text-gray-600">Aucun article trouvé</p></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <motion.div key={post.id} initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} transition={{ delay:index*0.1 }}>
                  <div className="overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all group h-full flex flex-col rounded-xl bg-white">
                    <div className="relative h-48 overflow-hidden bg-gradient-to-br from-black to-gray-800">
                      {post.featured_image ? <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                        : <div className="w-full h-full flex items-center justify-center"><BookOpen className="w-16 h-16 text-white/30" /></div>}
                      {post.category && (
                        <div className="absolute top-4 left-4">
                          <span className={`${categoryColors[post.category]} px-3 py-1 rounded-full text-sm font-medium`}>{categoryLabels[post.category]}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                        <div className="flex items-center gap-1"><Calendar className="w-4 h-4" /><span>{format(new Date(post.created_date),'dd MMM yyyy',{locale:fr})}</span></div>
                        <div className="flex items-center gap-1"><User className="w-4 h-4" /><span>{post.created_by}</span></div>
                      </div>
                      <h3 className="text-xl font-bold mb-2 text-black line-clamp-2">{post.title}</h3>
                      <p className="text-gray-600 mb-4 line-clamp-3 flex-1">{post.excerpt || post.content?.substring(0,150) + '...'}</p>
                      <Link to={`/blogpost?id=${post.id}`}>
                        <button className="w-full border-2 border-black text-black py-2 rounded-lg hover:bg-black hover:text-white flex items-center justify-center gap-2">
                          Lire la suite<ArrowRight className="w-4 h-4" />
                        </button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
